import React from 'react';
import PropTypes from 'prop-types';
import ReactHtmlParser from 'react-html-parser';
import get from 'lodash/get';


import BookGenresThemes from 'rebook/apps/books/components/BookGenresThemes';
import { formatDateTime } from 'rebook/apps/utils/datetime';
import pluralize from 'rebook/apps/utils/text/pluralize';
import BookRightholder from 'rebook/apps/books/components/BookRightholder';
import SocialShareMenu from 'rebook/apps/base/components/SocialShareMenu';
import TextTruncate from 'rebook/apps/base/components/TextTruncate';
import uaDetector from 'rebook/apps/utils/uaDetector';

import styles from './style.scss';


const BookSeoTitle = ({ book }) => {
  const isAudioBook = book.type === 'audio';
  const authorName = get(book, 'authors[0].genitive_full_name', '');

  if (isAudioBook) {
    return (
      <p>
        Слушайте онлайн полную версию аудиокниги&nbsp;
        {`«${book.name}» автора ${authorName} на сайте электронной библиотеки MyBook.ru.`}&nbsp;
        {`Скачивайте приложения для iOS или Android и слушайте «${book.name}» где угодно даже без интернета.`}
      </p>
    );
  }
  else {
    return (
      <p>
        Читайте онлайн полную версию книги&nbsp;
        {`«${book.name}» автора ${authorName} на сайте электронной библиотеки MyBook.ru.`}&nbsp;
        {`Скачивайте приложения для iOS или Android и читайте «${book.name}» где угодно даже без интернета.`}
      </p>
    );
  }
};

const BookDetailMeta = ({ book }) => (
  <React.Fragment>
    <div className={styles.bookDetailItem}>
      <h3>Подробная информация</h3>
    </div>
    <div className={styles.meta}>
      <div className={styles.metaBlock}>
        {book.translators && book.translators.length > 0 && (
          <p className={styles.metaParagraph}>
            {pluralize(book.translators.length, 'Переводчик', 'Переводчики', 'Переводчики')}{': '}
            {book.translators.map((translator, index) => (
              `${translator.cover_name}${index < book.translators.length - 1 ? ', ' : ''}`
            ))}
          </p>
        )}
        {book.written_dt && (
          <p className={styles.metaParagraph}>Дата написания: {book.written_dt.slice(0, 4)}</p>
        )}
        {book.partner_planned_dt && (
          <p className={styles.metaParagraph}>Год издания: {book.partner_planned_dt.slice(0, 4)}</p>
        )}
        {book.ean && (
          <p className={styles.metaParagraph}>ISBN (EAN): {book.ean}</p>
        )}
      </div>
      <div className={styles.metaBlock}>
        {book.is_readable_from && (
          <p className={styles.metaParagraph}>
            Дата поступления: {formatDateTime(book.is_readable_from, 'DD MMMM YYYY')}
          </p>
        )}
        {book.character_count && (
          <p className={styles.metaParagraph}>
            Объем: {
              book.character_count > 1000000
                ? `${Number(book.character_count / 1000000).toFixed(1)} млн знаков`
                : `${Number(book.character_count / 1000).toFixed(1)} тыс. знаков`
            }
          </p>
        )}
        {book.admin_url && (
          <React.Fragment>
            <p className={styles.metaParagraph}>ID: <a className="link" href={book.admin_url}>{book.id}</a></p>
            {book.weight && (
              <p className={styles.metaParagraph}>Вес: {book.weight}</p>
            )}
          </React.Fragment>
        )}
      </div>
    </div>
  </React.Fragment>
);

const BookDetailAnnotation = ({ book, user }) => {
  const userAgent = navigator && navigator.userAgent ? navigator.userAgent : null;
  if (!userAgent) {
    return null;
  }
  const isMobile = uaDetector.isMobile(userAgent);
  return (
    <div className={styles.annotationBlock}>
      <div className={styles.descriptionWrapper}>
        <h2>{book.type === 'audio' ? 'Об аудиокниге' : 'О книге'}</h2>
        {isMobile ?
          <TextTruncate collapseLabel="Свернуть">
            {ReactHtmlParser(book.annotation)}
            <BookSeoTitle book={book} />
            <BookDetailMeta book={book} />
          </TextTruncate>
          :
          <React.Fragment>
            {ReactHtmlParser(book.annotation)}
            <BookSeoTitle book={book} />
            <BookDetailMeta book={book} />
          </React.Fragment>
        }
        <BookGenresThemes book={book} user={user} />
      </div>
      <div className={styles.rightHolderContainer}>
        <BookRightholder book={book} user={user} />
        <div className={styles.shareLinks}>
          <SocialShareMenu
            url={book.share_url}
            utmCampaign={book.type === 'audio' ? 'audio' : 'book'}
            imageUrl={book.default_cover} />
        </div>
      </div>
    </div>
  );
};

BookDetailAnnotation.propTypes = {
  book: PropTypes.shape({
    annotation: PropTypes.string,
    genres: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
      })
    ),
    tags: PropTypes.array,
  }).isRequired,
};

export default BookDetailAnnotation;
